import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import {
  LayoutDashboard, Package, Store, ShoppingCart, Truck, RotateCcw,
  LogOut, Bell, Zap, Sparkles, ShoppingBasket, BarChart3,
  Users, LifeBuoy, Crown, Wallet, ShieldCheck, Globe, ChevronLeft, ChevronRight, KeyRound, Settings,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ChangePasswordDialog } from "@/components/ChangePassword";

const NAV = [
  { to: "/agent", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/agent/basket", label: "Basket", icon: ShoppingBasket },
  { to: "/agent/products", label: "Products", icon: Package },
  { to: "/agent/ai-studio", label: "AI Studio", icon: Sparkles },
  { to: "/agent/store", label: "My Store", icon: Store },
  { to: "/agent/marketplace", label: "Marketplace", icon: Globe },
  { to: "/agent/orders", label: "Orders", icon: ShoppingCart },
  { to: "/agent/shipments", label: "Shipments", icon: Truck },
  { to: "/agent/returns", label: "Returns", icon: RotateCcw },
  { to: "/agent/wallet", label: "Wallet", icon: Wallet },
  { to: "/agent/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/agent/customers", label: "Customers", icon: Users },
  { to: "/agent/support", label: "Support", icon: LifeBuoy },
  { to: "/agent/plan", label: "My Plan", icon: Crown },
  { to: "/agent/kyc", label: "KYC", icon: ShieldCheck },
  { to: "/agent/settings", label: "Settings", icon: Settings },
];

export default function AgentLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const [cpOpen, setCpOpen] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const updateArrows = () => {
    const el = navRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, []);
  const { data: notifs } = useQuery({
    queryKey: ["agent-notifs"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 30000,
  });

  return (
    <div className="flex min-h-screen flex-col bg-[#F2F6FB] text-[#0F2440]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg, #FF9933 0%, #FF9933 33.3%, #FFFFFF 33.3%, #FFFFFF 66.6%, #138808 66.6%, #138808 100%)" }} />
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 shadow-[0_2px_16px_rgba(15,42,92,0.06)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="DropPartner" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5 shadow-sm" />
            <div>
              <p className="text-sm font-bold tracking-tight text-[#0F2440]">DropPartner</p>
              <p className="text-[10px] uppercase tracking-widest text-[#E85D04]">Agent Hub</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="agent-change-password-btn" onClick={() => setCpOpen(true)} title="Change password"
              className="text-slate-400 transition-colors hover:text-[#E85D04]">
              <KeyRound size={16} />
            </button>
            <div className="relative">
              <Bell size={17} className="text-slate-500" />
              {notifs?.unread > 0 && (
                <span data-testid="agent-notif-count" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[9px] font-bold text-white">
                  {notifs.unread}
                </span>
              )}
            </div>
            <div className="hidden text-right sm:block">
              <p className="text-xs font-medium text-[#0F2440]">{user?.agent?.business_name || user?.name}</p>
              <p className="text-[10px] text-slate-500">{user?.email}</p>
            </div>
            <button data-testid="agent-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
              className="text-slate-400 transition-colors hover:text-rose-600">
              <LogOut size={16} />
            </button>
          </div>
        </div>
        <nav className="relative mx-auto max-w-6xl px-2 pb-2" data-testid="agent-nav-wrap">
          <button data-testid="nav-scroll-left" aria-label="Scroll menu left"
            onClick={() => navRef.current?.scrollBy({ left: -220, behavior: "smooth" })}
            className={`absolute -left-1 top-0 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-[#FF6B00] text-white shadow-md transition-opacity hover:bg-[#E85D04] ${canLeft ? "opacity-100" : "pointer-events-none opacity-0"}`}>
            <ChevronLeft size={15} strokeWidth={3} />
          </button>
          <div ref={navRef} onScroll={updateArrows} className="flex gap-1 overflow-x-auto px-6" style={{ scrollbarWidth: "none" }} data-testid="agent-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`agent-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  isActive ? "bg-[#FF6B00] text-white shadow-[0_4px_14px_rgba(255,107,0,0.35)]" : "text-slate-700 hover:bg-orange-50 hover:text-[#E85D04]"
                }`
              }>
              <n.icon size={13} /> {n.label}
            </NavLink>
          ))}
          </div>
          <button data-testid="nav-scroll-right" aria-label="Scroll menu right"
            onClick={() => navRef.current?.scrollBy({ left: 220, behavior: "smooth" })}
            className={`absolute -right-1 top-0 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-[#FF6B00] text-white shadow-md transition-opacity hover:bg-[#E85D04] ${canRight ? "opacity-100" : "pointer-events-none opacity-0"}`}>
            <ChevronRight size={15} strokeWidth={3} />
          </button>
        </nav>
      </header>
      <ChangePasswordDialog open={cpOpen} onClose={() => setCpOpen(false)} />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
